import { useCallback } from 'react';
import { ExportFormat } from '@ecosystem/newron-client-lib';
import {
  BubbleChartParam,
  BubbleChartResp,
  ColumnHeaderDisplayNameForOR,
  ColumnHeaderForOR,
  OpportunityReviewTableContent,
  OpportunityReviewTableResponse,
  RetailerType,
  SaleType,
  TableParam,
} from '@models/OpportunityReview';
import { getExportOpportunityReviewBubbleChart } from '@services/Chart.service';
import {
  displayExportErrorToastMessage,
  getAppliedFiltersText,
  getExportChartTypeAndParam,
  getKpiDisplayNameFromHeaderKey,
} from '@hooks/export/ExportUtils';
import { getVolumeBasedOnPeriodType } from '@utils/factVolumeUtils/FactVolumeUtils';
import { getDefaultValueForDerivablePeriodicity, getPeriodicityForMATAndYTD } from '@utils/PeriodUtils';
import { FactRevenue, Option, PageName, PageType } from '@utils/pageConfig/Types';
import {
  modifiedORTableContentsGrandTotalToTotalValue,
  modifiedPAORSegmentExportGrandTotalToTotalValue,
} from '@utils/TotalUtils';
import { ExportPerformanceBySegmentResp } from '@data-visualization/performance-analysis-line-chart/Types';
import { SaleTypeMapping } from '@const/PerformanceType';
import { ExportBubbleChartOptions } from '@hooks/export/export-bubble-chart/UseExportBubbleChart';
import getComparisonPeriod from '@components/filter-selection-note/GetComparisonPeriod';
import { getOpportunityReviewTable } from '@services/OpportunityReview.service';
import { OPPORTUNITY_REVIEW_CARD_TITLE } from '@pages/Utilities';
import { ToastType } from '@components/toast-message/ToastMessage';

export interface UseOpportunityReviewChartDownloadParams {
  swotTableParam: TableParam | undefined;
  swotChartParam: BubbleChartParam | undefined;
  swotTableTotalSize: number;
  displayTable: boolean;
  saleType: SaleType;
  bubbleSliderCurrentPeriod: string;
  comparisonPeriod: any;
  periodicity: any;
  period: any;
  exportTable: any;
  exportBubbleChart: any;
  invokeExport: any;
  appliedCountries: Option[];
  appliedProductGroups: Option[];
  feature: any;
  filters: any;
  pagesConfig: any;
  handleExportedTracking: (exportFormats: ExportFormat[], elementKPI: string) => void;
  tableData: any;
  getSummaryDataRowsForExport: (response: OpportunityReviewTableResponse) => OpportunityReviewTableContent[];
  getGroupedDataRowsForExport: (response: OpportunityReviewTableResponse) => OpportunityReviewTableContent[];
  getSegmentBy: (retailerType: string) => string;
  retailerType: RetailerType;
  bubbleChartData: BubbleChartResp;
  showToastMessage: ({ message, type, timeout }: { message: string; type?: ToastType; timeout?: number }) => void;
  factRevenue: FactRevenue;
}

export function useOpportunityReviewChartDownload({
  swotTableParam,
  swotChartParam,
  swotTableTotalSize,
  displayTable,
  saleType,
  bubbleSliderCurrentPeriod,
  comparisonPeriod,
  periodicity,
  period,
  exportTable,
  exportBubbleChart,
  invokeExport,
  appliedCountries,
  appliedProductGroups,
  feature,
  filters,
  pagesConfig,
  handleExportedTracking,
  tableData,
  getSummaryDataRowsForExport,
  getGroupedDataRowsForExport,
  getSegmentBy,
  retailerType,
  bubbleChartData,
  showToastMessage,
  factRevenue,
}: UseOpportunityReviewChartDownloadParams) {
  const onChartDownload = useCallback(
    async (exportFormats: ExportFormat[]): Promise<void> => {
      if (!swotTableParam || !swotChartParam) {
        return;
      }
      const timePeriod = period.label;
      const periodType = getPeriodicityForMATAndYTD(period, periodicity).periodicity;
      //update tableParam to call the API again with totalElements received from last call
      const requestPayload: TableParam = swotTableParam;
      requestPayload.pageRequest.size = swotTableTotalSize;
      // Even if the user select VOLUME from the UI, this API call should still call with VALUE
      //Since the API going to return both data
      const exportSwotChartParam: BubbleChartParam = {
        ...swotChartParam,
        saleType: SaleType.VALUE,
      };

      const exportTableData = async () => {
        const comparisonTimePeriod = getComparisonPeriod(period, periodType, comparisonPeriod);
        const dataForExport: OpportunityReviewTableResponse = await getOpportunityReviewTable(requestPayload);
        const data: OpportunityReviewTableContent[] = dataForExport.content!.content;
        const groupedData: OpportunityReviewTableContent[] = getGroupedDataRowsForExport(dataForExport);
        const summaryData: OpportunityReviewTableContent[] = getSummaryDataRowsForExport(dataForExport);

        const modifiedData = modifiedORTableContentsGrandTotalToTotalValue(data);

        const selectedKpiOption: Option = {
          value: pagesConfig.sortingOptions.headerKey,
          label: getKpiDisplayNameFromHeaderKey(
            pagesConfig.sortingOptions.headerKey,
            getVolumeBasedOnPeriodType(filters, periodicity),
          ),
        };

        await exportTable({
          exportFormats,
          invokeExport,
          title: OPPORTUNITY_REVIEW_CARD_TITLE,
          appliedCountry: appliedCountries,
          appliedPg: appliedProductGroups,
          data: [...groupedData, ...modifiedData],
          summaryData: summaryData,
          timePeriod,
          comparisonTimePeriod,
          factRevenue: factRevenue,
          periodType,
          showPerformanceWithin: feature.value,
          appliedFiltersText: getAppliedFiltersText(pagesConfig, PageType.OPPORTUNITY_REVIEW),
          selectedKpi: selectedKpiOption,
          pageName: PageName.OPPORTUNITY_REVIEW,
          pagesConfig: pagesConfig,
          volume: getVolumeBasedOnPeriodType(filters, periodicity),
        });

        handleExportedTracking(
          exportFormats,
          ColumnHeaderDisplayNameForOR(getVolumeBasedOnPeriodType(filters, periodicity))[
            pagesConfig.sortingOptionsOpportunityReview.headerKey as ColumnHeaderForOR
          ],
        );
      };

      const exportBubbleChartData = async () => {
        const dataForExport: ExportPerformanceBySegmentResp =
          await getExportOpportunityReviewBubbleChart(exportSwotChartParam);

        const modifiedDataForExport = modifiedPAORSegmentExportGrandTotalToTotalValue(dataForExport);

        const selectedKpi =
          saleType === SaleType.VALUE
            ? SaleTypeMapping()[SaleType.VALUE]
            : SaleTypeMapping(getVolumeBasedOnPeriodType(filters, periodicity))[SaleType.VOLUME];
        const kpiOption: Option = {
          label: selectedKpi,
          value: selectedKpi,
        };

        const defaultPeriodType = getDefaultValueForDerivablePeriodicity(periodType);

        const comparisonTimePeriod = getComparisonPeriod(period, periodType, comparisonPeriod);

        const options: ExportBubbleChartOptions = {
          exportFormats,
          invokeExport,
          title: OPPORTUNITY_REVIEW_CARD_TITLE,
          appliedCountry: appliedCountries,
          appliedPg: appliedProductGroups,
          data: modifiedDataForExport.kpiDataDto,
          timePeriod,
          comparisonTimePeriod,
          factRevenue: factRevenue,
          periodType: defaultPeriodType,
          showPerformanceWithin: feature.value,
          appliedFiltersText: getAppliedFiltersText(pagesConfig, PageType.OPPORTUNITY_REVIEW),
          selectedKpi: kpiOption,
          segmentBy: getSegmentBy(retailerType), //since we don't have a segment by for OR use this for the value of display by
          volume: getVolumeBasedOnPeriodType(filters, periodicity),
          pageName: PageName.OPPORTUNITY_REVIEW,
        };

        await exportBubbleChart(options);
        handleExportedTracking(exportFormats, kpiOption.label);
      };

      try {
        if (displayTable) {
          await exportTableData();
        } else {
          await exportBubbleChartData();
        }
      } catch (err: any) {
        const tableOrChartParam = getExportChartTypeAndParam(displayTable, swotTableParam, exportSwotChartParam);
        displayExportErrorToastMessage(
          err,
          `${PageName.OPPORTUNITY_REVIEW}- ${tableOrChartParam.type} error.`,
          tableOrChartParam.param,
          showToastMessage,
        );
      }
    },
    [
      swotTableParam,
      tableData,
      swotTableTotalSize,
      displayTable,
      bubbleChartData,
      retailerType,
      bubbleSliderCurrentPeriod,
      comparisonPeriod,
      periodicity,
    ],
  );

  return { onChartDownload };
}
